// @ts-strict-ignore
import { cloneDeep } from 'lodash/fp';
import type { WorkflowState } from 'flow/store/redux/state';
import { getAttribute } from 'flow/service/services';
import { extractDataRouteIds } from 'flow/service/dataRoutes/dataRoutes';
import { extractThresholdIds } from 'flow/service/thresholds/thresholds';
import type {
  Action,
  DataRoute,
  ReasonCode,
  ReasonCodeGroup,
  ServicesByName,
  Threshold,
} from '../../../store/engine/types';
import { isActionAnImportedValue } from '../../../service/actions';
import { ActionType } from '../../../store/engine/constants';

export function getSuccessMsg(type: 'partial' | 'full' | 'copy', isContainingDataPassingNodes: boolean) {
  let message;
  if (type === 'copy') {
    message = 'Successfully copied workflow to the clipboard.';
  } else if (type === 'partial') {
    message = 'The tags and rules you pasted are successfully imported and merged.';
  } else {
    message = 'The workflow was successfully imported. Please save to store it as a new version.';
  }

  return isContainingDataPassingNodes
    ? `${message} This workflow uses imported data. And if these nodes do not exist upstream in your journey, or this workflow is not a in journey, these values will default to 0 or an empty string.`
    : message;
}

export function checkExistenceOfDataPassingActions(actions: Record<string, Action>) {
  return Object.values(actions).some(isActionAnImportedValue);
}

function recursivelyGetDependentActions({
  dependencies,
  actions,
  nodeId,
}) {
  const dependentActions = {};
  dependencies[nodeId]?.forEach(dependentNode => {
    const dependentActionObject = cloneDeep(actions[dependentNode.id]);
    if (!dependentActions[dependentActionObject.publicId]) {
      dependentActions[dependentActionObject.publicId] = dependentActionObject;
    }

    const moreDependents = recursivelyGetDependentActions({
      dependencies,
      actions,
      nodeId: dependentNode.id,
    });

    Object.entries(moreDependents).forEach(([dependentKey, dependentValue]) => {
      if (!dependentActions[dependentKey]) {
        dependentActions[dependentKey] = dependentValue;
      }
    });
  });

  return dependentActions;
}

export function getSelectedActionAndRelevantData({
  selectedNode,
  workflowData,
}: {
  selectedNode: string;
  workflowData: WorkflowState['workflowData'];
}) {
  const thresholdsForCopy = {};
  const dataRoutesForCopy = {};

  const {
    actions,
    thresholds,
    dataRoutes,
    dependencies,
  } = workflowData;

  const actionObject = cloneDeep(actions[selectedNode]);
  const allDependentActions: Record<string, Action> = recursivelyGetDependentActions({
    dependencies,
    actions,
    nodeId: selectedNode,
  });
  const actionsForCopy: Record<string, Action> = {
    [actionObject.publicId]: actionObject,
    ...allDependentActions,
  };

  Object.entries(actionsForCopy).forEach(actionObjForCopy => {
    const actionObjForCopyValue = actionObjForCopy[1];
    if (Object.values(actionObjForCopyValue?.rules)?.length) {
      const allThresholdIdsOfThisAction = extractThresholdIds(actionObjForCopyValue?.rules);
      allThresholdIdsOfThisAction.forEach(thresholdId => {
        thresholdsForCopy[thresholdId] = thresholds[thresholdId];
      });
    }

    if (actionObjForCopyValue?.output) {
      const allDataRouteIdsOfThisAction = extractDataRouteIds(
        actionObjForCopyValue?.output.expression);
      allDataRouteIdsOfThisAction.forEach(dataRouteId => {
        dataRoutesForCopy[dataRouteId] = dataRoutes[dataRouteId];
      });
    }
  });

  return {
    actions: actionsForCopy,
    thresholds: thresholdsForCopy,
    dataRoutes: dataRoutesForCopy,
    partial: true,
  };
}

export function getAllRelevantSupplementalData({
  actions,
  thresholds,
  dataRoutes,
  services,
  reasonCodeGroups,
  reasonCodes,
}: {
  actions: Record<string, Action>;
  thresholds: Record<string, Threshold>;
  dataRoutes: Record<string, DataRoute>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
  reasonCodes: ReasonCode[];
}) {
  const allAttributes = {};
  const allReasonCodeGroups = {};
  Object.values(thresholds).forEach(threshold => {
    const leftAttrId = threshold.left.attributeId;
    if (leftAttrId) {
      allAttributes[leftAttrId] = getAttribute({
        actions,
        services,
        serviceActionId: threshold.left.actionId,
        attributeId: leftAttrId,
      });
    }

    const rightAttrId = threshold.right.attributeId;
    if (rightAttrId) {
      allAttributes[leftAttrId] = getAttribute({
        actions,
        services,
        serviceActionId: threshold.left.actionId,
        attributeId: leftAttrId,
      });
    }

    const rightReasonCodeGroupId = threshold.right.reasonCodeGroupId;
    if (rightReasonCodeGroupId) {
      const matchingReasonCodeGroup = reasonCodeGroups.find(
        reasonCodeGroup => reasonCodeGroup.id === rightReasonCodeGroupId,
      );

      const remappedReasonCodes = matchingReasonCodeGroup.reasonCodes.map(
        originalCode => {
          const matchingCodeObject = reasonCodes.find(
            code => code.reason_code_id === originalCode);
          if (matchingCodeObject) {
            return matchingCodeObject;
          }

          return originalCode;
        });
      allReasonCodeGroups[rightReasonCodeGroupId] = {
        ...matchingReasonCodeGroup,
        reasonCodes: remappedReasonCodes,
      };
    }
  });

  Object.values(dataRoutes).forEach(dataRoute => {
    const attrId = dataRoute.attribute.id;
    if (dataRoute.attribute.id) {
      allAttributes[attrId] = getAttribute({
        actions,
        services,
        serviceActionId: dataRoute.sourceId,
        attributeId: attrId,
      });
    }
  });

  return {
    serviceAttributes: allAttributes,
    reasonCodeGroups: allReasonCodeGroups,
  };
}

export function scrubServiceCredentials({
  actions,
  services,
}: {
  actions: Record<string, Action>;
  services: ServicesByName;
}) {
  const actionsWithNoCredentials: Record<string, Action> = {};
  const impactedServiceActions: string[] = [];
  Object.values(actions).forEach((action: Action) => {
    if (action.type === ActionType.SERVICE) {
      const matchingCredSchema = services[action.service.internalName].credentialsSchema;
      if (matchingCredSchema) {
        matchingCredSchema.credential_format?.secrets?.forEach(secretKey => {
          if (action.options?.[secretKey]) {
            action.options[secretKey].value = '';
            if (!impactedServiceActions.includes(action.service.name)) {
              impactedServiceActions.push(action.service.name);
            }
          }
        });
      }
    }
    actionsWithNoCredentials[action.publicId] = action;
  });

  return {
    actionsWithNoCredentials,
    impactedServiceActions,
  };
}
